import {
  definePage,
  redirect,
  type RouteLoaderOptions,
  type RouteLocation,
} from "@openclaw/uirouter";
import { createComponent } from "solid-js";
import { routePageSpec } from "../../app-route-paths.ts";
import type { SolidRouteProps } from "../../app-route-types.ts";
import type { ApplicationContext } from "../../app/context.ts";
import { formatUiError } from "../../lib/format-error.ts";
import { loadPluginCatalog } from "../../lib/plugins/index.ts";
import {
  canonicalPluginsRouteLocation,
  pluginsRouteLocation,
  type PluginsRouteData,
} from "./route-data.ts";

async function loadPluginsRouteData(
  context: ApplicationContext,
  options: RouteLoaderOptions,
): Promise<PluginsRouteData> {
  const location = pluginsRouteLocation(options.location);
  const gateway = context.gateway;
  const gatewaySnapshot = gateway.snapshot;
  const client = gatewaySnapshot.client;
  const data: PluginsRouteData = { gateway, gatewaySnapshot, result: null, error: null, location };
  if (gatewaySnapshot.phase === "connected" && client) {
    try {
      data.result = await loadPluginCatalog(client);
    } catch (error) {
      data.error = formatUiError(error);
    }
  }
  return data;
}

type PluginsSurface = "discovery" | "settings";

function definePluginsPage(routeId: "plugins" | "plugin-settings", surface: PluginsSurface) {
  return definePage({
    ...routePageSpec(routeId),
    loaderDeps: (_context: ApplicationContext, location: RouteLocation) => {
      const routeLocation = pluginsRouteLocation(location);
      return `${routeLocation.pathname}\u0000${routeLocation.search}\u0000${routeLocation.hash}`;
    },
    loader: (context: ApplicationContext, options: RouteLoaderOptions) => {
      const location = pluginsRouteLocation(options.location);
      const canonical = canonicalPluginsRouteLocation(location, context.basePath);
      if (canonical) {
        return redirect(canonical);
      }
      if (surface === "settings") {
        const configLoad = context.runtimeConfig.ensureLoaded();
        void configLoad
          .then(() => context.runtimeConfig.ensureSchemaLoaded())
          .catch(() => undefined);
      }
      return loadPluginsRouteData(context, options);
    },
    component: () =>
      import("./plugins-page.tsx").then((module) => ({
        header: true,
        renderSolid: (props: SolidRouteProps<PluginsRouteData>) =>
          createComponent(module.PluginsPage, {
            get routeData() {
              return props.data;
            },
            surface,
          }),
      })),
  });
}

const page = definePluginsPage("plugins", "discovery");
const settingsPage = definePluginsPage("plugin-settings", "settings");
export const pages = [page, settingsPage] as const;
